import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmAppMainPages } from '@pflegenavi/frontend/routing';
import {
  useFamilyMemberReceiptsPhoenix,
  useReceiptImages,
} from '@pflegenavi/frontend/api-nursing-home';
import type { ReceiptPhoenix } from '@pflegenavi/frontend/api-nursing-home';
import { ReceiptState } from '@pflegenavi/shared/api';
import { useUrlsToImages } from '@pflegenavi/shared-frontend/platform';
import { useFileUrlToTypeMapping } from '@pflegenavi/web-components';
import { useInitializeFM } from '../../hooks/useInitializeFM';
import { LatestPendingReceiptsView } from './LatestPendingReceiptsView';

interface LatestPendingReceiptsProps {
  receiptState?: ReceiptState;
}

export const LatestPendingReceipts: FC<LatestPendingReceiptsProps> = ({
  receiptState = ReceiptState.Pending,
}) => {
  const navigate = useNavigate();
  const { selectedResident } = useInitializeFM();
  const [receiptImageIds, setReceiptImageIds] = useState<string[] | undefined>(
    undefined
  );

  const selectedResidentId = selectedResident?.residentId;
  const tenantId = selectedResident?.tenant;

  const { data: receiptsData, isLoading } = useFamilyMemberReceiptsPhoenix(
    selectedResidentId,
    tenantId,
    { enabled: Boolean(selectedResidentId && tenantId) }
  );

  const receiptsOfDesiredState = useMemo(() => {
    if (!receiptsData?.data) {
      return [];
    }

    return receiptsData.data.filter(
      (receipt) => receipt.receipt_state === receiptState
    );
    // Show all receipts matching the desired state, no limit
  }, [receiptsData, receiptState]);

  const { receiptImages: receiptImageUrls, ImageUrlsLoading } = useUrlsToImages(
    {
      receiptImageIds,
      useReceiptImages,
    }
  );

  const imageUrlToTypeMap = useFileUrlToTypeMapping(receiptImageUrls);

  const handleReceiptImageClick = useCallback((imageIds: string[]) => {
    setReceiptImageIds(imageIds);
  }, []);

  const handleCloseImageGallery = useCallback(() => {
    setReceiptImageIds(undefined);
  }, []);

  const handleReceiptClick = useCallback(
    (receipt: ReceiptPhoenix) => {
      // Navigate to receipt detail view
      navigate(`${fmAppMainPages.TRANSACTIONS}/${receipt.id}`);
    },
    [navigate]
  );

  // Don't render if no receipts of desired state
  if (!isLoading && receiptsOfDesiredState.length === 0) {
    return null;
  }

  return (
    <LatestPendingReceiptsView
      isLoading={isLoading}
      data={receiptsOfDesiredState}
      onReceiptClick={handleReceiptClick}
      receiptImageUrls={receiptImageUrls || []}
      imageUrlsLoading={ImageUrlsLoading}
      imageUrlToTypeMap={imageUrlToTypeMap}
      receiptImageIds={receiptImageIds}
      onReceiptImageClick={handleReceiptImageClick}
      onCloseImageGallery={handleCloseImageGallery}
      receiptState={receiptState}
    />
  );
};
